import React, { useState, useMemo, Dispatch, SetStateAction } from "react"

import { GetStaticPaths, GetStaticProps } from "next"
import Image from "next/image"

import SEO from "components/SEO"
import { Box, Grid, Container } from "@mui/material"

import { anonymousAccessTokenCache } from "@secureo/common/utils/commerceTools/token/accessTokenCache"
import { supportedValidLocales } from "config/i18n"
import fetchSeries, { fetchSeriesSlugs } from "utils/commerceTools/fetchSeries"
import serializeJSON from "@secureo/common/utils/serializeJSON"
import fetchSecurityLevelInsurancesCustomObject, {
	SecurityLevelInsurance,
} from "@secureo/common/utils/commerceTools/graphql/queries/fetchSecurityLevelInsurancesCustomObject"

import { Series, SeriesModel } from "utils/commerceTools/formatters/formatSeries"
import StickySeriesNav from "containers/SeriesPage/StickySeriesNav"

// components
import TechDetails from "containers/SeriesPage/ATF/TechDetails"
import Overview from "containers/SeriesPage/BTF/Overview"
import Slider from "containers/SeriesPage/BTF/Slider"
import TopBackgroundElement from "containers/SeriesPage/ATF/TopBackgroundElement"
import BurglaryProtection from "containers/SeriesPage/BTF/BurglaryProtection"
import Closure from "containers/SeriesPage/BTF/Closure"
import Anchoring from "containers/SeriesPage/BTF/Anchoring"
import LockVariants from "containers/SeriesPage/BTF/LockVariants"
import ModelOverview from "containers/SeriesPage/BTF/ModelOverview"
import CompareModels from "containers/SeriesPage/BTF/CompareModels"
import BottomBackgroundElement from "containers/SeriesPage/BTF/BottomBackgroundElement"
import PrintProductSheet from "containers/SeriesPage/BTF/PrintProductSheet"

//
import { Select, FormControl } from "@mui/material"
import { makeStyles } from "makeStyles"

const useStyles = makeStyles()(() => ({
	responsiveHeadline: {
		marginBottom: 0,
		fontSize: 18,
		"@media only screen and (max-width: 960px)": {
			marginTop: 0,
			fontSize: "1.4rem",
		},
	},
	formControl: {
		minWidth: 250,
		"@media only screen and (max-width: 960px)": {
			width: "100%",
		},
	},
	icon: {
		color: "white",
	},
}))

const useSeriesModel = (models: SeriesModel[], initialModelSku?: string) => {
	const [modelSku, setModelSku] = useState(initialModelSku ?? models?.[0]?.sku)

	const stringifiedModels = JSON.stringify(models)
	const modelsBySku = useMemo(
		() =>
			models.reduce(
				(modelsBySku, model) => {
					return {
						...modelsBySku,
						[model?.sku ?? ""]: model,
					}
				},
				{} as { [sku: string]: SeriesModel },
			),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[stringifiedModels],
	)
	const model = modelsBySku?.[modelSku]

	return [model, setModelSku] as [SeriesModel, Dispatch<SetStateAction<string>>]
}

const SeriesPage = (props: Props) => {
	const { classes } = useStyles()
	const { series, securityLevelInsurances } = props
	const { slug, name, metaTitle, metaDescription, models, attributes: seriesAttributes } = series

	const [model, setModelSku] = useSeriesModel(models)

	const { name: modelName, assets, images, attributes, locks } = model

	const shortDescription = model?.attributes?.shortDescription ?? series?.shortDescription
	const atAGlanceContent =
		model?.attributes?.atAGlanceContent ?? seriesAttributes?.atAGlanceContent
	const bulgaryProtectionContent =
		model?.attributes?.bulgaryProtectionContent ?? seriesAttributes?.bulgaryProtectionContent
	const closureContent = model?.attributes?.closureContent ?? seriesAttributes?.closureContent
	const anchoringContent =
		model?.attributes?.anchoringContent ?? seriesAttributes?.anchoringContent

	const image = images?.[0] ?? null

	const asPath = `/s/${slug}`
	const hasModels = models.length > 1

	const lockVariant = locks[0]?.images[0]

	const onChange = (event) => {
		setModelSku(event.target.value)
	}

	return (
		<Box
			py={5}
			style={{
				background: "#042C33",
				color: "white",
				minHeight: 510,
				position: "relative",
				overflow: "hidden",
			}}
		>
			<SEO
				asPath={asPath}
				pageType="series"
				seriesName={name}
				title={metaTitle || name + " Serie"}
				description={metaDescription}
				appendLocalizedShopNameWithPipe
			/>
			<TopBackgroundElement />
			<Container>
				<div style={{ padding: "100px 0px" }}>
					<Grid container spacing={3}>
						<Grid item xs={12} md={5}>
							<div style={{ textTransform: "uppercase" }}>{name}</div>
							<h1 className={classes.responsiveHeadline}>{modelName}</h1>
							<p
								style={{ opacity: 0.75, lineHeight: 1.6 }}
								dangerouslySetInnerHTML={{ __html: shortDescription }}
							/>

							{hasModels && (
								<Box mt={4}>
									<h2 style={{ fontSize: 18, marginBottom: 5 }}>
										Modelle dieser Serie
									</h2>
									<FormControl className={classes.formControl}>
										<Select
											native
											value={model.sku}
											onChange={onChange}
											inputProps={{
												name: "model",
											}}
											style={{
												color: "white",
												padding: 0,
												border: "1px solid white",
											}}
											classes={{
												icon: classes.icon,
											}}
											variant="outlined"
											margin="none"
										>
											{models.map((product) => {
												if (!product) return null

												const { sku } = product
												const shortModelName =
													product.name.split(series.name)?.[1] ??
													product?.name

												return (
													<option key={sku} value={sku}>
														{shortModelName
															? (
																	series.name + shortModelName
																)?.replace("Secureo", "")
															: product?.name}
													</option>
												)
											})}
										</Select>
									</FormControl>
								</Box>
							)}
							<PrintProductSheet
								model={model}
								models={models}
								shortDescription={shortDescription}
								atAGlanceContent={atAGlanceContent}
							/>
						</Grid>
						<Grid item xs={6} md={5} style={{ textAlign: "center", zIndex: 100 }}>
							{image && (
								<Image
									alt={modelName}
									src={image}
									width={300}
									height={300}
									style={{
										maxWidth: "100%",
										height: "auto",
										objectFit: "contain",
									}}
								/>
							)}
						</Grid>
						<Grid item xs={6} md={2}>
							<TechDetails attributes={attributes} />
						</Grid>
					</Grid>
				</div>
			</Container>
			<StickySeriesNav
				modelSku={model?.sku}
				setModelSku={setModelSku}
				models={models}
				// TODO
				hasTechnicalDetails={true}
				hasDescription={true}
				hasLockVariants={locks.length > 0}
				hasOverview={true}
			/>
			<Overview attributes={attributes} atAGlanceContent={atAGlanceContent} />
			<div id="product-description">
				<Slider images={images} />
				{bulgaryProtectionContent && (
					<BurglaryProtection
						bulgaryProtectionContent={bulgaryProtectionContent}
						securityGrade={attributes?.securityGrade}
						securityLevelInsurances={securityLevelInsurances}
					/>
				)}
				{closureContent && (
					<Closure closureContent={closureContent} lockVariant={lockVariant} />
				)}
				{anchoringContent && <Anchoring anchoringContent={anchoringContent} />}
			</div>
			{locks.length > 0 && (
				<div id="lock-variants">
					<LockVariants locks={locks} image={image} />
				</div>
			)}
			{assets?.length > 0 && (
				<div id="downloads">
					<Box my={5} style={{ position: "relative", zIndex: 1 }}>
						<Container>
							<h3 style={{ fontSize: 25 }}>Downloads</h3>
							<ul>
								{assets.map((asset) => {
									const { url, name } = asset

									return (
										<li key={url}>
											<a style={{ color: "#fff" }} href={url}>
												{name}
											</a>
										</li>
									)
								})}
							</ul>
						</Container>
					</Box>
				</div>
			)}
			{hasModels && (
				<div id="series-overview">
					<ModelOverview
						modelSku={model.sku}
						setModelSku={setModelSku}
						models={models}
						name={name}
					/>
					<CompareModels models={models} name={name} />
				</div>
			)}
			<BottomBackgroundElement />
		</Box>
	)
}

export const getStaticPaths: GetStaticPaths = async () => {
	const accessToken = await anonymousAccessTokenCache()

	const slugs = await fetchSeriesSlugs(accessToken)
	const filteredSlugs = slugs.filter((slug) => !["sec-navproduct"].includes(slug))

	const paths = []
	for (const slug of filteredSlugs) {
		for (const locale of supportedValidLocales) {
			paths.push({ params: { slug }, locale })
		}
	}

	return {
		paths,
		fallback: false,
	}
}

export const getStaticProps: GetStaticProps = async (context) => {
	const accessToken = await anonymousAccessTokenCache()
	const locale = context.locale as string
	const slug = context.params?.slug as string

	const series = await fetchSeries(accessToken, slug)

	if (!series || series?.models?.length === 0) {
		return {
			notFound: true,
			props: {},
		}
	}

	const securityLevelInsurances = await fetchSecurityLevelInsurancesCustomObject(accessToken)

	const props: Props = {
		locale,
		series,
		securityLevelInsurances,
	}

	return serializeJSON({ props, revalidate: 10 })
}

interface Props {
	locale: string
	series: Series
	securityLevelInsurances: SecurityLevelInsurance[]
}

export default SeriesPage
